<script setup lang="ts">
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { i18n } from '@/i18n';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { useRouter } from 'vue-router';

// Composables
import { useTemplateSelectableItems } from '../composables/useTemplateSelectableItems';
import { useTemplateDragDropReorder } from '../composables/useTemplateDragDropReorder';
import { useTemplatePreviewHeaders } from '../composables/useTemplatePreviewHeaders';

// Types and Constants
import { OutputFormat, IMasterItem, IOutputDataItem, PreviewHeaderType } from '../domains/entities/InventoryTemplate';
import {
  getInitialMasterItems,
  getInitialOutputDataItems,
  MAX_PREVIEW_HEADERS,
  isSingleFieldOutputKey,
} from '../constants/inventoryTemplateConstants';

// Template functions
import {
  exportInventoryExcel,
  downloadExcelFromUrl,
  ExportInventoryExcelParams,
  getTemplateFilterState,
  clearTemplateFilterState,
} from './useInventoryStore/inventoryTemplate';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import axios from 'axios';

const t = i18n.t;
const router = useRouter();

/**
 * プレビューヘッダーのラベルを取得（i18n対応）
 * @param header - プレビューヘッダー
 * @returns 翻訳されたラベル
 */
const getHeaderLabel = (header: {
  type: string;
  masterKey?: string;
  outputDataKey?: string;
  yearMonth?: { year: number; month: number };
}): string => {
  if (header.type === 'master' && header.masterKey) {
    return t(header.masterKey);
  }
  if (header.type === 'outputData' && header.outputDataKey) {
    if (header.yearMonth) {
      return `${t('displayYearMonth', { year: header.yearMonth.year, month: header.yearMonth.month })}_${t(header.outputDataKey)}`;
    }
    return t(header.outputDataKey);
  }
  return '';
};

// Master Items - using composable
const {
  items: masterItems,
  selectedItems: selectedMasterItems,
  handleItemChange: handleMasterItemChange,
  reorderItems: reorderMasterItems,
} = useTemplateSelectableItems<IMasterItem>(getInitialMasterItems());

// Output Data Items - using composable
const {
  items: outputDataItems,
  selectedItems: selectedOutputDataItems,
  handleItemChange: handleOutputDataItemChange,
  reorderItems: reorderOutputDataItems,
  moveSingleFieldItem: moveOutputDataSingleFieldItem,
} = useTemplateSelectableItems<IOutputDataItem>(getInitialOutputDataItems());

// Output Settings
const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
const startYearMonth = ref<IYearMonth>(currentYearMonth);
const endYearMonth = ref<IYearMonth>(DateService.getShiftedIYearMonthByMonths(currentYearMonth.year, currentYearMonth.month, 3));
const outputFormat = ref<OutputFormat>(OutputFormat.BY_MONTH);

const locale = computed(() => i18n.locale);

// Preview Headers - using composable
const { previewHeaders, showPreview, otherColumnsCount } = useTemplatePreviewHeaders(
  selectedMasterItems,
  selectedOutputDataItems,
  startYearMonth,
  endYearMonth,
  outputFormat
);

// Drag and Drop - using composable
const { draggedHeaderId, dragOverIndex, handleDragStart, handleDragEnd, handleDragOver, handleDrop } = useTemplateDragDropReorder();

/**
 * ドロップハンドラーのラッパー
 * @param event - ドラッグイベント
 * @param targetIndex - ターゲットインデックス
 */
const onDrop = (event: DragEvent, targetIndex: number) => {
  handleDrop(event, targetIndex, previewHeaders.value, reorderMasterItems, reorderOutputDataItems, moveOutputDataSingleFieldItem);
};

// Tooltip State for NX Explanation
const showTooltip = ref(false);
const tooltipRef = ref<HTMLElement | null>(null);

/**
 * ツールチップの表示切り替え
 */
const toggleTooltip = () => {
  showTooltip.value = !showTooltip.value;
};

/**
 * ツールチップを非表示にする（外部クリック検出用）
 * @param event - マウスイベント
 */
const handleClickOutside = (event: MouseEvent) => {
  if (tooltipRef.value && event.target && !tooltipRef.value.contains(event.target as Node)) {
    showTooltip.value = false;
  }
};

/**
 * ダウンロードボタンの有効性判定
 */
const isDownloadEnabled = computed(() => {
  const hasMasterItem = selectedMasterItems.value.length > 0;
  const hasOutputData = selectedOutputDataItems.value.length > 0;
  const hasPeriod = startYearMonth.value && endYearMonth.value;
  const hasFormat = outputFormat.value !== null;

  return hasMasterItem && hasOutputData && hasPeriod && hasFormat;
});

/**
 * 単一フィールド項目の挿入位置を計算
 * プレビューヘッダーから単一フィールド項目の位置を取得し、
 * 月別ヘッダー配列内でのインデックスとして返す
 */
const singleFieldPositions = computed<{ [key: string]: number } | undefined>(() => {
  // 出力データ項目のみを抽出（マスタ項目を除く）
  const outputHeaders = previewHeaders.value.filter((h) => h.type === PreviewHeaderType.OUTPUT_DATA);

  // 単一フィールド項目の位置を計算
  const positions: { [key: string]: number } = {};

  outputHeaders.forEach((header, idx) => {
    if (isSingleFieldOutputKey(header.outputDataKey)) {
      // この単一フィールド項目より前にある月別ヘッダーの数がインデックス
      const monthlyHeadersBefore = outputHeaders.slice(0, idx).filter((h) => !isSingleFieldOutputKey(h.outputDataKey)).length;
      positions[header.outputDataKey!] = monthlyHeadersBefore;
    }
  });

  return Object.keys(positions).length > 0 ? positions : undefined;
});

/**
 * 戻るボタンのハンドラー
 */
const handleBack = () => {
  router.back();
};

// Loading state
const isDownloading = ref(false);

/**
 * エラーの種類を判定してユーザーフレンドリーなメッセージを表示
 * @param error - エラーオブジェクト
 */
const handleExportError = (error: unknown) => {
  // Axiosタイムアウトエラーの判定
  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      toast.error(t('データ量が多いため処理がタイムアウトしました。出力期間を短くするか、項目数を減らしてください。'));
      return;
    }
    // ネットワークエラー（接続なし）
    if (error.code === 'ERR_NETWORK' || !error.response) {
      toast.error(t('ネットワークエラーが発生しました。接続を確認してください。'));
      return;
    }
    // サーバーエラー (5xx)
    if (error.response && error.response.status >= 500) {
      toast.error(t('サーバーエラーが発生しました。しばらく待ってから再度お試しください。'));
      return;
    }
  }
  // その他のエラー
  toast.error(t('Excelエクスポートに失敗しました'));
};

/**
 * Excelダウンロードボタンのハンドラー
 */
const handleDownload = async () => {
  if (!isDownloadEnabled.value || isDownloading.value) return;

  isDownloading.value = true;

  try {
    // Build params for inventoryTemplate
    const params: ExportInventoryExcelParams = {
      masterItems: selectedMasterItems.value.map((item) => item.key),
      outputDataItems: selectedOutputDataItems.value.map((item) => item.key),
      startYearMonth: startYearMonth.value,
      endYearMonth: endYearMonth.value,
      outputFormat: outputFormat.value,
      singleFieldPositions: singleFieldPositions.value,
      language: locale.value === 'en' ? 'en' : 'ja',
      filterItems: getTemplateFilterState().filterItems,
    };

    const result = await exportInventoryExcel(params);

    if (result.status.type === 'success') {
      const { download_url, file_name } = result.status.data;
      downloadExcelFromUrl(download_url, file_name);
    } else {
      // API returned error response
      const error = result.status.error;
      if (error.statusCode === 504 || error.statusCode === 408) {
        toast.error(t('データ量が多いため処理がタイムアウトしました。出力期間を短くするか、項目数を減らしてください。'));
      } else if (error.statusCode >= 500) {
        toast.error(t('サーバーエラーが発生しました。しばらく待ってから再度お試しください。'));
      } else {
        toast.error(t('Excelエクスポートに失敗しました'));
      }
      console.error('Export failed:', error);
    }
  } catch (error) {
    console.error('Export error:', error);
    handleExportError(error);
  } finally {
    isDownloading.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  // フィルター情報をクリア
  clearTemplateFilterState();
});
</script>

<template>
  <section class="inventory-template-page">
    <header class="inventory-template-page__header">
      <h1 class="inventory-template-page__title">{{ t('データ出力') }}</h1>
    </header>

    <!-- Output Preview Section -->
    <section v-if="showPreview" class="inventory-template-page__preview">
      <h2 class="inventory-template-page__preview-title">{{ t('出力プレビュー') }}</h2>
      <p class="inventory-template-page__preview-description">
        {{ t('出力ヘッダーの並びを表示しています。') }}
      </p>
      <div class="inventory-template-page__preview-headers">
        <span
          v-for="(header, index) in previewHeaders.slice(0, MAX_PREVIEW_HEADERS)"
          :key="header.id"
          class="inventory-template-page__preview-tag"
          :class="{
            'inventory-template-page__preview-tag--dragging': draggedHeaderId === header.id,
            'inventory-template-page__preview-tag--drag-over': dragOverIndex === index,
          }"
          :draggable="true"
          @dragstart="handleDragStart($event, header)"
          @dragend="handleDragEnd($event)"
          @dragover.prevent="handleDragOver($event, index)"
          @drop="onDrop($event, index)"
        >
          {{ getHeaderLabel(header) }}
        </span>
        <span v-if="otherColumnsCount > 0" class="inventory-template-page__preview-tag inventory-template-page__preview-tag--more">
          ...{{ t('他{count}列', { count: otherColumnsCount }) }}
        </span>
      </div>
      <div class="inventory-template-page__preview-actions">
        <button
          class="inventory-template-page__download-button"
          :class="{
            'inventory-template-page__download-button--disabled': !isDownloadEnabled || isDownloading,
            'inventory-template-page__download-button--loading': isDownloading,
          }"
          :disabled="!isDownloadEnabled || isDownloading"
          @click="handleDownload"
        >
          {{ isDownloading ? t('ダウンロード中...') : t('Excelをダウンロード') }}
          <SvgLoader v-if="!isDownloading" name="downloadSmall" width="16" height="16" />
        </button>
        <div class="inventory-template-page__back-button-wrapper">
          <TextButton :button-style="'btn-secondary'" :size="'btn-md'" :on-click="handleBack">
            {{ t('戻る') }}
          </TextButton>
        </div>
      </div>
    </section>

    <!-- Three Column Sections -->
    <div class="inventory-template-page__sections">
      <!-- Master Items Section -->
      <section class="inventory-template-page__section">
        <h3 class="inventory-template-page__section-title">{{ t('マスタ項目') }}</h3>
        <p class="inventory-template-page__section-description">
          {{ t('出力する項目を選択してください') }}
        </p>
        <div class="inventory-template-page__checkbox-grid">
          <label v-for="item in masterItems" :key="item.key" class="inventory-template-page__checkbox-item">
            <input
              type="checkbox"
              :checked="item.checked"
              @change="handleMasterItemChange(item, ($event.target as HTMLInputElement).checked)"
            />
            <span>{{ t(item.key) }}</span>
          </label>
        </div>
      </section>

      <!-- Output Data Section -->
      <section class="inventory-template-page__section">
        <h3 class="inventory-template-page__section-title">
          <span>{{ t('出力データ') }}</span>
          <div ref="tooltipRef" class="inventory-template-page__tooltip-wrapper">
            <SvgLoader name="questionIcon" class="inventory-template-page__help-icon" @click="toggleTooltip" />
            <div v-if="showTooltip" class="inventory-template-page__tooltip">
              <p>{{ t('客先内示(N-1)とは、受領月が前月の内示を表示します。') }}</p>
              <p>{{ t('例: 2025年10月の内示を表示する場合、受領月は2025年9月の内示が表示されます。') }}</p>
              <p>{{ t('仕入先内示(N-1)とは、作成月が前月の内示を表示します。') }}</p>
            </div>
          </div>
        </h3>
        <p class="inventory-template-page__section-description">
          {{ t('出力する数値データを選択してください') }}
        </p>
        <div class="inventory-template-page__checkbox-grid">
          <label v-for="item in outputDataItems" :key="item.key" class="inventory-template-page__checkbox-item">
            <input
              type="checkbox"
              :checked="item.checked"
              @change="handleOutputDataItemChange(item, ($event.target as HTMLInputElement).checked)"
            />
            <span>{{ t(item.key) }}</span>
          </label>
        </div>
      </section>

      <!-- Output Settings Section -->
      <section class="inventory-template-page__section">
        <h3 class="inventory-template-page__section-title">
          <span>{{ t('出力設定') }}</span>
          <SvgLoader name="chevronRight" class="inventory-template-page__arrow-icon" />
        </h3>
        <p class="inventory-template-page__section-description">
          {{ t('出力期間と出力形式を選択してください') }}
        </p>
        <div class="inventory-template-page__settings-content">
          <div class="inventory-template-page__period">
            <label class="inventory-template-page__period-label">{{ t('出力期間') }}</label>
            <div class="inventory-template-page__period-inputs">
              <div class="inventory-template-page__period-item">
                <label class="inventory-template-page__period-item-label">{{ t('開始月') }}</label>
                <MonthPicker
                  :model-value="startYearMonth"
                  :locale="locale"
                  prop-format="yyyy / MM"
                  :max-date="endYearMonth"
                  @update:model-value="startYearMonth = $event"
                />
              </div>
              <div class="inventory-template-page__period-item">
                <label class="inventory-template-page__period-item-label">{{ t('終了月') }}</label>
                <MonthPicker
                  :model-value="endYearMonth"
                  :locale="locale"
                  prop-format="yyyy / MM"
                  :min-date="startYearMonth"
                  @update:model-value="endYearMonth = $event"
                />
              </div>
            </div>
          </div>
          <div class="inventory-template-page__format">
            <label class="inventory-template-page__format-label">{{ t('出力形式') }}</label>
            <div class="inventory-template-page__format-options">
              <label
                class="inventory-template-page__radio-item"
                :class="{ 'inventory-template-page__radio-item--selected': outputFormat === 'byMonth' }"
              >
                <input
                  type="radio"
                  name="outputFormat"
                  value="byMonth"
                  :checked="outputFormat === 'byMonth'"
                  @change="outputFormat = 'byMonth'"
                />
                <div class="inventory-template-page__radio-content">
                  <span class="inventory-template-page__radio-label">{{ t('月単位で横並びにまとめる') }}</span>
                  <span class="inventory-template-page__radio-example">
                    {{ t('例:1月(在庫、販売実績...)→2月(在庫、販売実績...)') }}
                  </span>
                </div>
              </label>
              <label
                class="inventory-template-page__radio-item"
                :class="{ 'inventory-template-page__radio-item--selected': outputFormat === 'byDataType' }"
              >
                <input
                  type="radio"
                  name="outputFormat"
                  value="byDataType"
                  :checked="outputFormat === 'byDataType'"
                  @change="outputFormat = 'byDataType'"
                />
                <div class="inventory-template-page__radio-content">
                  <span class="inventory-template-page__radio-label">{{ t('データ種類単位で横並びにまとめる') }}</span>
                  <span class="inventory-template-page__radio-example">
                    {{ t('例:在庫(1月、2月...)→販売実績(1月、2月...)') }}
                  </span>
                </div>
              </label>
            </div>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<style scoped lang="scss">
.inventory-template-page {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
    gap: 0;
  }

  &__title {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: $textDefault;
  }

  &__preview {
    background-color: $semanticWhite;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    padding: 24px;
    display: grid;
    gap: 16px;
  }

  &__preview-title {
    font-size: 18px;
    font-weight: 700;
    color: $textDefault;
    margin: 0;
  }

  &__preview-description {
    font-size: 14px;
    color: $textDefault;
    margin: 0;
  }

  &__preview-headers {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 8px 0;
    flex-wrap: wrap;
  }

  &__preview-tag {
    background-color: #f5f5f5;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    padding: 6px 12px;
    font-size: 12px;
    white-space: nowrap;
    color: $textDefault;
    cursor: move;
    transition: all 0.2s;

    &:hover {
      background-color: #eeeeee;
    }

    &--dragging {
      opacity: 0.5;
    }

    &--drag-over {
      border-color: $semanticPrimary;
      background-color: $semanticPrimarySubdued;
    }

    &--more {
      color: #666;
      font-style: italic;
      cursor: default;

      &:hover {
        background-color: #f5f5f5;
      }
    }
  }

  &__preview-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-start;
  }

  &__back-button-wrapper {
    :deep(.button_content) {
      box-shadow: none !important;
    }
  }

  &__download-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #0066cc;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover:not(&--disabled) {
      background-color: #0052a3;
    }

    &--disabled {
      background-color: #cccccc;
      color: #666666;
      cursor: not-allowed;
      opacity: 0.6;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  &__sections {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    align-items: start;
  }

  &__section {
    background-color: $semanticWhite;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    padding: 24px;
    display: grid;
    gap: 16px;
    align-content: start;
    height: fit-content;
  }

  &__section-title {
    font-size: 18px;
    font-weight: 700;
    color: $textDefault;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  &__help-icon {
    width: 18px;
    height: 18px;
    color: #616161;
    cursor: pointer;
    flex-shrink: 0;
    transition: color 0.2s;

    &:hover {
      color: #333333;
    }
  }

  &__tooltip-wrapper {
    position: relative;
    display: inline-block;
  }

  &__tooltip {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background-color: #333333;
    color: white;
    padding: 12px 16px;
    border-radius: 4px;
    font-size: 12px;
    white-space: normal;
    width: 280px;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    p {
      margin: 0 0 8px 0;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &::before {
      content: '';
      position: absolute;
      bottom: 100%;
      right: 12px;
      border: 6px solid transparent;
      border-bottom-color: #333333;
    }
  }

  &__arrow-icon {
    width: 16px;
    height: 16px;
    color: #666;
  }

  &__section-description {
    font-size: 14px;
    color: $textSecondary;
    margin: 0;
  }

  &__checkbox-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;

    &--two-columns {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: $textDefault;
    cursor: pointer;

    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
  }

  &__settings-content {
    display: grid;
    gap: 24px;
  }

  &__period {
    display: grid;
    gap: 12px;
  }

  &__period-label {
    font-size: 14px;
    font-weight: 600;
    color: $textDefault;
    margin-bottom: 4px;
  }

  &__period-inputs {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  &__period-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__period-item-label {
    font-size: 12px;
    color: $textDefault;
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__format {
    display: grid;
    gap: 12px;
  }

  &__format-label {
    font-size: 14px;
    font-weight: 600;
    color: $textDefault;
    margin-bottom: 4px;
  }

  &__format-options {
    display: grid;
    gap: 12px;
  }

  &__radio-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    padding: 12px;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    background-color: $semanticWhite;
    transition: all 0.2s ease;

    &--selected {
      border: 1px solid $semanticPrimary;
      background-color: $semanticPrimarySubdued;
    }
  }

  &__radio-content {
    display: grid;
    gap: 4px;
    flex: 1;
  }

  &__radio-label {
    font-size: 14px;
    color: $textDefault;
    font-weight: 500;
  }

  &__radio-example {
    font-size: 12px;
    color: #666;
  }

  input[type='radio'] {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    cursor: pointer;
    flex-shrink: 0;
  }
}
</style>
